import React, { useState } from 'react';
import { Row, Col, Button, List, Popconfirm, Modal, Divider, Grid } from 'antd';
import { DeleteOutlined, DownloadOutlined, FileSearchOutlined, ShareAltOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';

import { DocumentoService } from '../Services/Domain/Documentos/DocumentoService';
import { GestaoDeGUIDs } from './GestaoDeGUIDs';

import Arquivo from '../Models/Pessoa/Sistema/Arquivo';
import Formatacoes from '../Utils/Formatacao';
import { Preview } from './Preview';
import Typography from 'antd/es/typography/Typography';

interface GestaoDeArquivosProps {
    arquivos: Arquivo[];
    desativarFn: (codigo: number) => any;
    depoisDeDesativar?: () => any;

    listProps?: React.ComponentProps<typeof List>;
    permitirCompartilhamento?: (arquivo: Arquivo) => boolean
}

export const GestaoDeArquivos: React.FC<GestaoDeArquivosProps> = (props) => {
    const { arquivos } = props;
    const breakpoint = Grid.useBreakpoint();

    const desativarArquivoMutation = useMutation({
        mutationFn: (arquivo: { codigo: number }) => props.desativarFn(arquivo.codigo),
        onSuccess: () => props.depoisDeDesativar?.(),
    });

    const [ statusDoModalDeDetalhes, setStatusDoModalDeDetalhes ] = useState<{exibir: boolean, data?: {id: string}}>({exibir: false})
    const guidsDoArquivo = useQuery({
        queryKey: ["DocumentoService.listarGUIDsDoArquivo", statusDoModalDeDetalhes.data?.id],
        queryFn: () => {
            if(statusDoModalDeDetalhes.data?.id == undefined) return Promise.reject();
            return DocumentoService.listarGUIDsDoArquivo(statusDoModalDeDetalhes.data.id)
        },
        enabled: statusDoModalDeDetalhes.data?.id != undefined
    })

    return (
        <React.Fragment>
            <Modal
                open={statusDoModalDeDetalhes.exibir}
                destroyOnClose
                width={breakpoint.lg? "50vw" : "80vw"}
                title="Detalhes"
                maskClosable={false}
                okButtonProps={{}}
                onOk={() => setStatusDoModalDeDetalhes({ exibir: false })}
                onCancel={() => setStatusDoModalDeDetalhes({ exibir: false })}
                footer={(_, { OkBtn }) => <OkBtn />}
            >
                <GestaoDeGUIDs
                    guids={guidsDoArquivo.data?.data ?? []}
                    adicionar={() => {
                        if (statusDoModalDeDetalhes.data?.id == undefined) return Promise.reject();
                        return DocumentoService.compartilharArquivo(statusDoModalDeDetalhes.data.id, {});
                    }}
                    depoisDeAdicionar={() => {
                        guidsDoArquivo.refetch();
                    }}
                    depoisDeDesativar={() => guidsDoArquivo.refetch()}
                    gerarBtnProps={{
                        loading: guidsDoArquivo.isLoading,
                    }}
                    listProps={{
                        loading: guidsDoArquivo.isLoading,
                        locale: {
                            emptyText: "Nenhum link para compartilhamento foi gerado",
                        },
                    }}
                />

                {statusDoModalDeDetalhes.data?.id && (
                    <React.Fragment>
                        <Divider/>
                        <Preview.PeloGUID nomeGUID={statusDoModalDeDetalhes.data.id} />
                    </React.Fragment>
                )}
            </Modal>
            <Row>
                <Col xs={24}>
                    <List
                        {...props.listProps}
                        dataSource={arquivos}
                        renderItem={(arquivo) => {
                            const link = DocumentoService.URLDoArquivo(arquivo.nome);
                            const permiteCompartilhar = props.permitirCompartilhamento?.(arquivo) === true;
                            return (
                                <List.Item
                                    key={arquivo.nome}
                                    // className="common-list-item common-list-item-render"
                                    actions={[
                                        <Popconfirm title="Tem certeza?" onConfirm={() => desativarArquivoMutation.mutate({ codigo: arquivo.codigo })}>
                                            <Button type="link" icon={<DeleteOutlined />} disabled={desativarArquivoMutation.isPending}>
                                                Excluir
                                            </Button>
                                        </Popconfirm>,

                                        permiteCompartilhar && (
                                            <Button
                                                type="link"
                                                icon={<FileSearchOutlined />}
                                                onClick={() => setStatusDoModalDeDetalhes({ exibir: true, data: { id: arquivo.nome } })}
                                            >
                                                Detalhes
                                            </Button>
                                        ),

                                        <a href={link} target="_blank" rel="noopener noreferrer">
                                            <DownloadOutlined />
                                        </a>,
                                    ]}
                                >
                                    <List.Item.Meta
                                        title={<Typography>{arquivo.nomeOriginal}</Typography>}
                                        description={`Tamanho: ${Formatacoes.bytesParaHumanos(arquivo.tamanho)} - Criado em: ${Formatacoes.paraData(
                                            arquivo.dataCriacao
                                        )?.toLocaleDateString()}`}
                                    />
                                </List.Item>
                            );
                        }}
                    />
                </Col>
            </Row>
        </React.Fragment>
    );
};

export default GestaoDeArquivos;
